import { Component, ComponentRef, ViewChild, ViewContainerRef } from '@angular/core';
import { CompareHelper } from '@barthauer/abc/core';
import { TargetColumnValue } from '@barthauer/abc/core/data';
import {
  AbcContextMenu,
  AbcDateBoxComponent,
  AbcFormComponent,
  IForm,
  ItemType,
  ValidationStatus,
  _AbcContextMenuComponent,
} from '@barthauer/abc/ui-components';
import { GoToComponent } from '../../../go-to/go-to.component';
import { TargetLayoutInfo } from '../../../higon/higon.model';
import { ColumnObject, LayoutColumn, SpecialType } from '../../../models/layout.model';
import { NavigationLayoutViewModel } from '../../../navigation/navigation.viewModel';
import { StandardComponent } from '../../../shared/type';
import { LayoutColumnViewModel, SubLayoutViewModel } from '../../../viewModels/layout.viewModel';
import { DataFormService } from '../../data-form.service';
import { DynamicTabsDirective, DynamicTopDirective } from '../dynamic.directive';
import { DataFormSettings } from '../settings.model';

@Component({
  template: '',
  standalone: false,
})
export abstract class UiCreatorBase {
  @ViewChild(DynamicTopDirective, { static: true }) protected fixedTopDirective!: DynamicTopDirective;
  @ViewChild(DynamicTabsDirective, { static: true }) protected tabsDirective!: DynamicTabsDirective;
  @ViewChild('abcWindowContainer', { read: ViewContainerRef }) protected abcWindow: ViewContainerRef;
  private _arenaGoTo: GoToComponent;
  private get arenaGoTo(): GoToComponent {
    if (!this._arenaGoTo) {
      const cpt = this._service.arena.loadComponent(GoToComponent, this.tabsDirective);
      this._arenaGoTo = <GoToComponent>cpt.componentRef.instance;
      document.getElementById('dataFormComponent').appendChild(cpt.domElement);
    }
    return this._arenaGoTo;
  }
  private static mouseDown: any;
  private styleableHtmlElements: Map<HTMLElement, LayoutColumnViewModel> = new Map<
    HTMLElement,
    LayoutColumnViewModel
  >();
  private styleableMutationObserver: MutationObserver;

  private requiredControls: Map<{ validationStatus: ValidationStatus | string; value: any }, LayoutColumnViewModel> =
    new Map<{ validationStatus: ValidationStatus | string; value: any }, LayoutColumnViewModel>();
  private isValidationActive: boolean;

  constructor(
    private _service: DataFormService,
    private _settings: DataFormSettings,
  ) {
    this._settings.appendValidatorAction((isActive) => this.validateColumnValues(isActive));
  }

  protected applyHtmlStyle(domElement: HTMLElement, column: LayoutColumnViewModel, isChildElement?: boolean): boolean {
    if (!column.backColor && !column.color && !column.fontBold) {
      return false;
    }
    const name = domElement.nodeName?.toLowerCase();
    if (isChildElement && name.includes('-') && name !== 'abc-check-box') {
      return false;
    }
    let result = false;
    let cssText = '';
    let cssBackColor = '';

    if (column.backColor && column.dataType !== 'boolean') {
      const controlBackgroundColorVariable = getComputedStyle(document.documentElement)
        .getPropertyValue('--abcControlBackgroundColor')
        .trim();
      cssBackColor =
        CompareHelper.isNullorEmpty(controlBackgroundColorVariable) || column.backColor == 'transparent'
          ? 'background-color: ' + column.backColor + '!important;'
          : 'background-color: var(--abcControlBackgroundColor) !important;';
      cssText += cssBackColor;
    }
    if (column.color) {
      cssText += 'color: ' + column.color + ' !important;';
    }
    if (column.fontBold) {
      cssText += 'font-weight: bold !important;';
    }
    if (cssText) {
      if (name === 'td') {
        domElement.style.cssText += cssBackColor;
      } else if (name === 'input' || domElement.className?.includes('dx-checkbox-text')) {
        domElement.style.cssText += cssText;
        result = true;
      }
      if (!result) {
        for (const child of Array.from(domElement.childNodes)) {
          if (child instanceof HTMLElement) {
            if (this.applyHtmlStyle(child, column, true)) {
              result = true;
            }
          }
        }
      }
      if (!result && !isChildElement) {
        if (!this.styleableMutationObserver) {
          this.styleableMutationObserver = new MutationObserver((arr) => {
            for (const r of arr) {
              if (r.target instanceof HTMLElement) {
                let el = r.target;
                while (el) {
                  const c = this.styleableHtmlElements.get(el);
                  if (c) {
                    this.styleableHtmlElements.delete(el);
                    this.applyHtmlStyle(r.target, c, true);
                    this.styleableHtmlElements.set(el, c);
                    break;
                  } else {
                    el = el.parentElement;
                  }
                }
              }
            }
          });
        }

        this.styleableMutationObserver.observe(domElement, {
          subtree: true,
          childList: true,
        });
        this.styleableHtmlElements.set(domElement, column);
      }
    }
    return result;
  }

  protected appendContextMenu(domElement: HTMLElement, column: LayoutColumnViewModel) {
    const inputContextMenu: AbcContextMenu[] = [];
    const uiconfig = this._service.abcArena.actions?.getUIConfiguration
      ? this._service.abcArena.actions?.getUIConfiguration()
      : null;
    if (column.layoutColumnObject.LayoutColumn.GridColumnSettings) {
      inputContextMenu.push({
        id: 'addToTable',
        text: this._service.arena.res('DataForm-AddToRemoveFromTable'),
        visible: !uiconfig?.isStandaloneDataForm && uiconfig?.renderHigna !== false,
        onItemClick: () => {
          this._service.abcArena.actions.showHideColumn(column);
        },
      });
    }
    let targets: { layout: NavigationLayoutViewModel; target: TargetLayoutInfo }[];
    const goToWindowContextMenu: AbcContextMenu = {
      id: 'goToWindow',
      text: this._service.arena.res('DataForm-GoToWindow'),
      onItemClick: (e) => {
        return this.arenaGoTo.showTargetNavigationLayouts(targets, true);
      },
    };
    inputContextMenu.unshift(goToWindowContextMenu);
    const goToContextMenu: AbcContextMenu = {
      id: 'goTo',
      text: this._service.arena.res('DataForm-GoTo'),
      visible: !uiconfig.isStandaloneDataForm,
      onItemClick: (e) => {
        return this.arenaGoTo.showTargetNavigationLayouts(targets, false);
      },
    };
    inputContextMenu.unshift(goToContextMenu);

    if (UiCreatorBase.mouseDown) {
      domElement.removeEventListener('mousedown', UiCreatorBase.mouseDown);
    }

    UiCreatorBase.mouseDown = domElement.addEventListener('mousedown', async (event) => {
      if (event.buttons === 2) {
        try {
          if (this._service.arena.loadContextMenu) {
            const mnu = this.createAbcContextMenu('UiCreatorBase.contextMenu');
            const contextMenu = [...inputContextMenu];
            if (targets) {
              for (const t of targets) {
                if (
                  (await this._service.data.abcCore.objectSpace.getObjectType(t.layout?.objectType).toPromise())
                    .allDerivedTypes.length > 1
                ) {
                  targets = null;
                  break;
                }
              }
            }
            if (!targets) {
              targets = await this.arenaGoTo.service.getPossibleTargetLayouts(
                this._service.abcArena.currentDataEntity?.ObjectType ??
                  this._settings.mainObjectTypeViewModel?.name ??
                  this._settings.layoutViewModel.objectType,
                column.oPath,
                this._service.abcArena.currentDataEntity?.Id,
                this.getTargetColumnValues(),
                this._settings.layoutViewModel,
              );
            }
            if (!targets?.length) {
              contextMenu.splice(0, 2);
            } else {
              if (contextMenu.length > 2) {
                contextMenu[2]['beginGroup'] = true;
              }
              if (targets.length === 1) {
                for (let i = 0; i < 2; i++) {
                  contextMenu[i].text = contextMenu[i].text?.replace('...', ' -> ' + targets[0].layout?.layoutCaption);
                }
              }
            }
            if (contextMenu.length) {
              mnu.m.dataSource = contextMenu;
              mnu.componentRef.instance.onItemClick.subscribe((item) => {
                if (
                  item.itemData.id === 'goTo' ||
                  item.itemData.id === 'goToWindow' ||
                  item.itemData.id === 'addToTable'
                ) {
                  contextMenu.forEach((menu, index) => {
                    if (menu.id === item.itemData.id) {
                      return contextMenu[index].onItemClick(item.event);
                    }
                  });
                }
              });
            }
          }
        } catch (err) {
          console.log(err);
        }
      }
    });
  }

  protected getTargetColumnValues(): TargetColumnValue[] {
    return [];
  }

  protected checkWhatToSaveInPicklist(data): string {
    if (data) {
      if (data[0]?.Id) return 'Id';
      if (data[0]?.Value) return 'Value';
      if (data[0]?.Identifier) return 'Identifier';
    }
  }

  protected checkWhatToShowInPicklist(data): string {
    if (data) {
      if (data[0]?.DisplayText___HIDDEN) return this._settings.DISPLAYTEXT_HIDDEN;
      if (data[0]?.Name) return 'Name';
      if (data[0]?.Description) return 'Description';
      if (data[0]?.Caption) return 'Caption';
      if (data[0]?.Bezeichnung) return 'Bezeichnung';
      if (data[0]?.Designation) return 'Designation';
      if (data[0]?.Identifier) return 'Identifier';
    }
  }

  protected createAbcContextMenu(id: string): {
    componentRef: ComponentRef<any>;
    domElement: HTMLElement;
    m: _AbcContextMenuComponent;
  } {
    const mnu = this._service.abcCoreContainer.createComponent(this.tabsDirective, _AbcContextMenuComponent);
    (<any>mnu.componentRef.instance).id = id;
    mnu.domElement.id = id;
    const m = <_AbcContextMenuComponent>mnu.componentRef.instance;
    m.width = '200';
    m.hideOnOutsideClick = true;

    m.onHidden.subscribe(() => {
      mnu.domElement?.remove();
      mnu.componentRef?.destroy();
    });

    this._settings.detectChanges();

    return { domElement: mnu.domElement, componentRef: mnu.componentRef, m: m };
  }

  protected createGroupCaption(
    td: HTMLTableCellElement,
    column: LayoutColumnViewModel | SubLayoutViewModel,
  ): HTMLElement {
    const formSettings: IForm = {
      id: 'form',
      caption: column.caption,
      itemType: ItemType.Group,
      setFormStyle: () => {},
    };
    const form = this._service.abcContainer.createComponent(this.tabsDirective, AbcFormComponent, formSettings);
    if (column instanceof LayoutColumnViewModel && column.labelLayoutSettings) {
      if (column.labelLayoutSettings.ColumnSpan) {
        td.colSpan = <number>column.labelLayoutSettings.ColumnSpan;
      }
      if (column.labelLayoutSettings.RowSpan) {
        td.rowSpan = <number>column.labelLayoutSettings.RowSpan;
      }
    } else if (column.layoutSettings) {
      if (column.layoutSettings.ColumnSpan) {
        td.colSpan = <number>column.layoutSettings.ColumnSpan;
      }
      if (column.layoutSettings.RowSpan) {
        td.rowSpan = <number>column.layoutSettings.RowSpan;
      }
    }
    return form.domElement;
  }

  protected getOpathValue(oPath: string) {
    for (let index = 0; index < this._service.LIST_START_KEY.length; index++) {
      const key = this._service.LIST_START_KEY[index];
      if (oPath?.indexOf(key) >= 0) {
        return {
          hasValue: (oPath = oPath?.replace(key, this._service.LIST_END_KEY[index])),
          startKey: key,
          endKey: this._service.LIST_END_KEY[index],
        };
      }
    }
  }

  protected createLabel(td: HTMLTableCellElement, column: LayoutColumnViewModel | SubLayoutViewModel) {
    const label: HTMLLabelElement = document.createElement('label');
    label.insertAdjacentText('afterbegin', column.caption);
    label.title = column.caption;
    label.style.width = '100%';
    label.style.whiteSpace = 'nowrap';
    label.style.textOverflow = 'ellipsis';
    label.style.display = 'inline-block';
    label.style.overflow = 'hidden';
    label.style.verticalAlign = 'middle';
    return label;
  }

  protected prepareLinkLabel(column: ColumnObject, data: any, retryCounter: number = 0): void {
    const node = this._settings.linkLabelsMap.get(column.Key).getElementsByClassName('dx-texteditor-input')[0];
    if (node?.parentElement) {
      const parentNode = node.parentElement;
      parentNode.removeChild(node);
      const a = document.createElement('a');
      a.setAttribute('contenteditable', 'false');
      a.classList.add('dx-texteditor-input');
      const componentInstance = {
        value: '',
        html: a,
      };
      const linkText = document.createTextNode(' ');
      a.appendChild(linkText);

      a.id = column.Key;
      // a.href = componentInstance.value;
      a.title = componentInstance.value;
      a.target = '_blank';
      (<StandardComponent>column.Control).value = data[column.Key];
      if (column.LayoutColumn?.Caption && column.LayoutColumn?.SpecialType === SpecialType.LinkLabel) {
        a.innerText = column.LayoutColumn.Caption;
        let lltextbox: HTMLElement = parentNode;
        do {
          lltextbox = lltextbox.parentElement;
        } while (lltextbox && lltextbox.localName !== 'dx-text-box');

        if (lltextbox?.className) {
          lltextbox.className = lltextbox.className.replace('floating-label', 'hidden-label');
          const labels = lltextbox.getElementsByClassName('dx-texteditor-label');
          if (labels?.length) {
            labels[0].remove();
          }
        }
      } else {
        a.innerText = data[column.Key];
      }

      a.href = this.prepareHref(column.LayoutColumn, data[column.Key]);

      parentNode.appendChild(a);

      a.addEventListener('input', (event: any) => {
        (<StandardComponent>column.Control).value = event.srcElement.innerText;
        a.href = this.prepareHref(column.LayoutColumn, event.srcElement.innerText);
      });
    } else if (retryCounter < 3) {
      setTimeout(() => {
        this.prepareLinkLabel(column, data, retryCounter + 1);
      }, 500);
    }
  }

  protected loadLayoutImagesCore(data: any) {
    this._settings.layoutViewModel.images = [];
    if (data?.length > 0) {
      data.sort((firstPicture, nextPicture) => {
        firstPicture.insertTime = new Date(
          firstPicture?.InsertAt?.replaceAll(firstPicture.InsertAt.substr(0, 10), firstPicture.Date),
        );
        nextPicture.insertTime = new Date(
          nextPicture?.InsertAt?.replaceAll(nextPicture.InsertAt.substr(0, 10), nextPicture.Date),
        );
        return nextPicture.insertTime - firstPicture.insertTime;
      });
      data.forEach((element) => {
        const image = new Image();
        image.src = 'data:image/png;base64,' + element.Object;
        image.height = 200;
        image.width = 200;
        this._settings.layoutViewModel.images.push(image.src);
      });
    }
    this._settings.layoutViewModel.images.push(this._settings.placeholderPath);
  }
  protected loadLayoutImages(): void {
    // if (!layout.images?.length || layout.images?.length < layout.layoutObject?.Documents.length) {
    this.loadLayoutImagesCore(this._settings.layoutViewModel.layoutObject?.Documents);
    // }
    this._service.cdrSubject.next(null);
  }

  private prepareHref(layoutColumn: LayoutColumn, href: string): string {
    if (!href) {
      return href;
    } else if (layoutColumn?.OPath?.includes('Mail') && layoutColumn?.OPath?.includes('mail')) {
      if (href?.startsWith('mailto:')) {
        return href;
      } else {
        return 'mailto:' + href;
      }
    } else if (!href?.includes(':') && !href?.startsWith('/')) {
      return '//' + href;
    } else {
      return href;
    }
  }

  // TODO moved to service
  protected validateOpath(subLayoutViewOpath: string) {
    return this._service.data.validateOpath(
      this._settings.mainObjectTypeViewModel?.name ?? this._settings.layoutViewModel.objectType,
      subLayoutViewOpath,
    );
  }

  protected isValidDate(abcDateBoxRef: AbcDateBoxComponent, OPath: string) {
    const hasDate = this._settings.dateBoxListMap.get('DATE-' + this.getOpathValue(OPath)?.hasValue);
    if (hasDate) {
      let timeList = [];
      hasDate.min = abcDateBoxRef?.value;
      setTimeout(() => {
        if (hasDate.getValidationStatus()) {
          this._settings.dateBoxListMap.forEach((value: AbcDateBoxComponent, key: string) => {
            if (
              key.indexOf(this.getOpathValue(OPath)?.startKey) >= 0 ||
              key.indexOf(this.getOpathValue(OPath)?.endKey) >= 0
            ) {
              timeList.push({ key: key, value: value });
            }
          });
          if (timeList.length === 2) {
            this.isValidTime(timeList[0].value, timeList[0].key);
          }
        }
      });
    }
  }

  protected isValidTime(abcDateBoxRef: AbcDateBoxComponent, OPath: string) {
    const hasTime = this._settings.dateBoxListMap.get('TIME-' + this.getOpathValue(OPath)?.hasValue);
    if (hasTime) {
      let dateList: AbcDateBoxComponent[] = [];
      this._settings.dateBoxListMap.forEach((value: AbcDateBoxComponent, key: string) => {
        if (
          key.indexOf(this.getOpathValue(OPath)?.startKey) >= 0 ||
          key.indexOf(this.getOpathValue(OPath)?.endKey) >= 0
        ) {
          dateList.push(value);
        }
      });
      if (dateList.length === 0 || dateList[0]?.value === dateList[1]?.value) {
        hasTime.min = abcDateBoxRef?.value;
      } else {
        hasTime.min = null;
      }
    }
  }
  protected setTdcolAndRowSpan(td: HTMLTableCellElement, column: LayoutColumnViewModel | SubLayoutViewModel): void {
    if (column.layoutSettings) {
      if (column.layoutSettings.ColumnSpan) {
        td.colSpan = <number>column.layoutSettings.ColumnSpan;
      }
      if (column.layoutSettings.RowSpan) {
        td.rowSpan = <number>column.layoutSettings.RowSpan;
      }
    }
  }

  protected addColumnForValidation(
    column: LayoutColumnViewModel,
    component: { validationStatus: ValidationStatus | string; value: any },
  ) {
    if (column && component) {
      const isColumnRequired = this._settings?.mainObjectTypeViewModel?.columnsByName?.get(column.oPath)?.isRequired;
      if (isColumnRequired) {
        this.requiredControls.set(component, column);
      }
    }
  }

  protected validateColumnValue(column: LayoutColumnViewModel, value: any) {
    if (this.isValidationActive) {
      for (const e of Array.from(this.requiredControls)) {
        if (e[1] === column) {
          this.validateColumnValueCore(e[1], e[0], value);
        }
      }
    }
  }

  private validateColumnValueCore(
    column: LayoutColumnViewModel,
    component: { validationStatus: ValidationStatus | string },
    value: any,
  ) {
    if (this.isValidationActive) {
      if (value) {
        component.validationStatus = ValidationStatus.Valid;
      } else {
        component.validationStatus = ValidationStatus.Invalid;
      }
    } else {
      component.validationStatus = ValidationStatus.Valid;
    }
  }

  private validateColumnValues(isActive: boolean): void {
    this.isValidationActive = isActive;
    for (const e of Array.from(this.requiredControls)) {
      this.validateColumnValueCore(e[1], e[0], e[0].value);
    }
  }
}

export enum Picklist {
  Value = 'Value',
  Description = 'Description',
}
